import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "./apiTypes";
import { unwrapData, unwrapList } from "./apiTypes";

export type AdminInstructorFinancialOverview = {
  currency: string;
  total_collected: number;
  pending_requests: number;
  pending_amount: number;
  total_paid_out: number;
  available_balance: number;
  active_enrollments: number;
  free_enrollments: number;
};

export type AdminInstructorFinancialTransaction = {
  id: number;
  type: "bundle" | "course" | string;
  status: string;
  payment_status?: string | null;
  payment_method?: "vodafone_cash" | "instapay" | string | null;
  payment_submitted_at?: string | null;
  amount?: number | null;
  expected_amount: number;
  currency: string;
  is_free: boolean;
  student?: { id: number; name?: string | null; phone?: string | null } | null;
  bundle?: { id: number; title?: string | null } | null;
  course?: { id: number; title?: string | null } | null;
  starts_at?: string | null;
  ends_at?: string | null;
  created_at?: string | null;
  reviewed_at?: string | null;
};

export type AdminInstructorFinancialTransactionsPage = PaginatedResult<AdminInstructorFinancialTransaction>;

export type AdminInstructorPayout = {
  id: number;
  amount: number;
  currency: string;
  payment_method: "cash" | "bank_transfer" | "vodafone_cash" | "instapay" | string;
  reference?: string | null;
  notes?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  admin?: { id: number; name?: string | null } | null;
};

function unwrapPaginated<T>(payload: ApiEnvelope<PaginatedResult<T>> | undefined): PaginatedResult<T> {
  const result = payload?.result as PaginatedResult<T> | { data?: PaginatedResult<T> } | undefined;
  if (result && "data" in result && result.data && !Array.isArray(result.data)) return result.data;
  return unwrapList<T>(payload);
}

export const adminInstructorFinancialService = {
  async overview(instructorId: number | string) {
    const response = await api.get<ApiEnvelope<{ data: AdminInstructorFinancialOverview }>>(
      `/mcp/instructors/${instructorId}/financial/overview`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminInstructorFinancialOverview>(response.data);
  },

  async transactions(instructorId: number | string, params: { page?: number; limit?: number; status?: string } = {}) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminInstructorFinancialTransaction>>>(
      `/mcp/instructors/${instructorId}/financial/transactions`,
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },
  async payouts(instructorId: number | string, params: { page?: number; limit?: number } = {}) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminInstructorPayout>>>(
      '/mcp/instructors/' + instructorId + '/financial/payouts',
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },
  async createPayout(instructorId: number | string, payload: {
    amount: number;
    payment_method: string;
    currency?: string;
    reference?: string | null;
    notes?: string | null;
    paid_at?: string | null;
  }) {
    const response = await api.post<ApiEnvelope<{ data: { payout: AdminInstructorPayout; overview: AdminInstructorFinancialOverview } }>>(
      '/mcp/instructors/' + instructorId + '/financial/payouts',
      payload,
      { meta: { toast: false } }
    );
    return unwrapData<{ payout: AdminInstructorPayout; overview: AdminInstructorFinancialOverview }>(response.data);
  },
};
